import type { RoleItem } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import { Button, Chip, Tooltip } from '@heroui/react';
import { RiAddLine, RiDeleteBinLine, RiEditLine } from '@remixicon/react';
import { useAtom, useAtomValue } from 'jotai';
import { toast } from 'react-toastify';

import { TablePage, TableFilters, ColumnsToggleDropdown } from '@/components/table';
import { rolesListParamsAtom, rolesQueryAtom, deleteRole, detachPermissionFromRole } from '@/store';

// Custom hooks
import { useRolesFilters, useRolesSorting, useRoleActions } from '@/hooks/roles';
import { useVisibleColumns } from '@/hooks/table';

// Components
import { RolePermissionsModal } from '@/components/roles/RolePermissionsModal';
import { ConfirmDeleteModal } from '@/components/shared/ConfirmDeleteModal';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { SearchInput } from '@/components/shared/SearchInput';
import { DateRangeFilter } from '@/components/shared/DateRangeFilter';

export default function RolesPage() {
  const { data, isLoading, isError, error } = useAtomValue(rolesQueryAtom);
  const [params, setParams] = useAtom(rolesListParamsAtom);

  const roles = React.useMemo<RoleItem[]>(() => {
    const allRoles = data?.data ?? [];
    // Filter out system roles that should not be displayed
    return allRoles.filter((role) => role.name !== 'customer' && role.name !== 'super_admin');
  }, [data]);

  // Custom hooks
  const {
    searchTerm,
    setSearchTerm,
    dateFilter,
    hasAnyFilter,
    handleSearch,
    handleClearSearch,
    clearFilters,
    handleDateChange,
    handlePageChange
  } = useRolesFilters();

  const { sortDescriptor, setSortDescriptor, sortedRoles } = useRolesSorting(roles);

  // Role actions hook
  const {
    isPermissionsModalOpen,
    selectedRole,
    openCreateModal,
    openEditModal,
    onPermissionsModalOpenChange,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    onDeleteModalOpenChange
  } = useRoleActions();

  const [, deleteRoleAtom] = useAtom(deleteRole);
  const [, detachPermissionAtom] = useAtom(detachPermissionFromRole);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isDetachingPermission, setIsDetachingPermission] = React.useState(false);
  const [permissionToDetach, setPermissionToDetach] = React.useState<{
    role: RoleItem;
    permission: { id: number; name: string };
  } | null>(null);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  // Delete role handler
  const handleDeleteRole = React.useCallback(async () => {
    if (!selectedRole) return;

    try {
      setIsDeleting(true);
      await deleteRoleAtom(selectedRole.id);
      triggerRefresh();
      closeDeleteModal();
    } catch (error) {
      console.error('Failed to delete role:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [selectedRole, deleteRoleAtom, triggerRefresh, closeDeleteModal]);

  // Detach permission handler
  const handleDetachPermission = React.useCallback(async () => {
    if (!permissionToDetach) return;

    try {
      setIsDetachingPermission(true);
      await detachPermissionAtom({
        roleId: permissionToDetach.role.id,
        body: { permission_ids: [permissionToDetach.permission.id] }
      });
      toast.success(`Permission "${permissionToDetach.permission.name}" removed successfully`);
      setPermissionToDetach(null);
      triggerRefresh();
    } catch (error) {
      console.error('Failed to detach permission:', error);
      toast.error(i18n.t('copy.failed_to_remove_permission'));
    } finally {
      setIsDetachingPermission(false);
    }
  }, [permissionToDetach, detachPermissionAtom, triggerRefresh]);

  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderCell = React.useCallback(
    (roleItem: RoleItem, columnKey: React.Key) => {
      switch (columnKey) {
        case 'name':
          return <span className="font-medium">{roleItem.name}</span>;
        case 'description':
          return roleItem.description ? (
            <span>{truncateText(roleItem.description, 40)}</span>
          ) : (
            <span className="text-xs text-gray-400">No description</span>
          );
        case 'permissions':
          return (
            <div className="flex flex-wrap gap-1">
              {roleItem.permissions
                .sort((a, b) => a.name.localeCompare(b.name))
                .slice(0, 3)
                .map((permission) => (
                  <Chip key={permission.id} color="primary" size="sm" variant="flat">
                    {permission.name}
                  </Chip>
                ))}
              {roleItem.permissions.length > 3 && (
                <Chip color="default" size="sm" variant="flat">
                  +{roleItem.permissions.length - 3} more
                </Chip>
              )}
            </div>
          );
        case 'users_count':
          return (
            <Chip color="secondary" size="sm" variant="flat">
              {roleItem.users_count} users
            </Chip>
          );
        case 'actions':
          return (
            <div className="relative flex items-center justify-end">
              <PermissionGuard permission="Role.update">
                <Tooltip content={i18n.t('copy.edit_role')}>
                  <Button isIconOnly size="sm" variant="light" onPress={() => openEditModal(roleItem)}>
                    <RiEditLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission="Role.destroy">
                <Tooltip content={i18n.t('copy.delete_role')}>
                  <Button isIconOnly size="sm" variant="light" color="danger" onPress={() => openDeleteModal(roleItem)}>
                    <RiDeleteBinLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        default:
          return null;
      }
    },
    [openEditModal, openDeleteModal]
  );

  const renderCollapseContent = React.useCallback((roleItem: RoleItem) => {
    return (
      <div className="mb-4 space-y-6">
        <div>
          <h4 className="mb-2 text-sm font-semibold">Permissions ({roleItem.permissions.length})</h4>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {roleItem.permissions
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((permission) => (
                  <Chip
                    key={permission.id}
                    color="primary"
                    size="sm"
                    variant="flat"
                    onClose={() => setPermissionToDetach({ role: roleItem, permission })}
                    className="cursor-pointer"
                  >
                    {permission.name}
                  </Chip>
                ))}
            </div>
            {roleItem.permissions.length === 0 && (
              <span className="text-xs text-gray-400">No permissions assigned</span>
            )}
          </div>
        </div>
      </div>
    );
  }, []);

  const columns = React.useMemo(
    () => [
      { name: i18n.t('columns.name'), uid: 'name', sortable: true },
      { name: i18n.t('copy.description'), uid: 'description', sortable: true },
      { name: i18n.t('copy.permissions'), uid: 'permissions', sortable: false },
      { name: i18n.t('common.actions'), uid: 'actions', align: 'end' }
    ],
    []
  );

  // Columns visibility management
  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns, [
    'name',
    'description',
    'permissions',
    'actions'
  ]);

  return (
    <>
      <TablePage
        columns={columns}
        currentPage={data?.meta?.current_page}
        data={sortedRoles}
        emptyMessage={i18n.t('copy.no_roles_found')}
        error={error}
        errorMessage={i18n.t('copy.failed_to_load_roles')}
        enableCollapse={true}
        renderCollapseContent={renderCollapseContent}
        filtersSection={
          <TableFilters
            hasActiveFilters={hasAnyFilter}
            isRefreshing={isLoading}
            onClearFilters={clearFilters}
            onRefresh={triggerRefresh}
          >
            <SearchInput
              placeholder={i18n.t('copy.search_roles')}
              value={searchTerm}
              onValueChange={setSearchTerm}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />

            <ColumnsToggleDropdown
              allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
              selected={visibleColumns}
              onChange={(next) => setVisibleColumns(next)}
            />

            {/* <DateRangeFilter
              value={dateFilter}
              onChange={handleDateChange}
            /> */}
          </TableFilters>
        }
        headerColumns={headerColumns}
        isError={isError}
        isLoading={isLoading}
        renderCell={renderCell}
        sortDescriptor={sortDescriptor}
        title={i18n.t('nav.roles')}
        titleActions={
          <Button
            color="success"
            size="sm"
            variant="flat"
            startContent={<RiAddLine className="h-4 w-4" />}
            onPress={openCreateModal}
          >
            Add Role
          </Button>
        }
        totalPages={data?.meta?.last_page}
        onPageChange={handlePageChange}
        onRefresh={triggerRefresh}
        onSortChange={(d) => setSortDescriptor(d)}
      />

      <RolePermissionsModal
        isOpen={isPermissionsModalOpen}
        onOpenChange={onPermissionsModalOpenChange}
        role={selectedRole}
        onSuccess={() => {
          triggerRefresh();
        }}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onOpenChange={onDeleteModalOpenChange}
        title={i18n.t('copy.delete_role_1')}
        message={`Are you sure you want to delete the role "${selectedRole?.name}"? This action cannot be undone.`}
        confirmText={i18n.t('common.delete')}
        cancelText="Cancel"
        isLoading={isDeleting}
        onConfirm={handleDeleteRole}
      />

      <ConfirmDeleteModal
        isOpen={Boolean(permissionToDetach)}
        onOpenChange={(open) => {
          if (!open) setPermissionToDetach(null);
        }}
        title={i18n.t('copy.remove_permission')}
        message={`Are you sure you want to remove the permission "${permissionToDetach?.permission.name}" from the role "${permissionToDetach?.role.name}"?`}
        confirmText={i18n.t('copy.remove')}
        cancelText="Cancel"
        isLoading={isDetachingPermission}
        onConfirm={handleDetachPermission}
      />
    </>
  );
}
